import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, RefreshCw, UserMinus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  unsubscribe: 'Unsubscribed',
  bounce: 'Bounced',
  spam_complaint: 'Spam complaint',
  admin_delete: 'Admin delete',
  ban: 'Banned',
  account_deletion: 'Account deleted',
};

const REASON_COLOUR = {
  unsubscribe: 'bg-zinc-800 text-zinc-300',
  bounce: 'bg-orange-950/60 text-orange-300 border border-orange-800/50',
  spam_complaint: 'bg-red-950/60 text-red-300 border border-red-800/50',
  admin_delete: 'bg-blue-950/60 text-blue-300 border border-blue-800/50',
  ban: 'bg-red-950/60 text-red-300 border border-red-800/50',
  account_deletion: 'bg-purple-950/60 text-purple-300 border border-purple-800/50',
};

const fmtSydney = (row) => {
  const raw = row.removed_at_sydney || row.removed_at;
  if (!raw) return '-';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Audit log of every subscriber/account removal - nothing is deleted
 *  silently. Filter by date range and reason, export to CSV. */
const RemovalLogCard = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [byReason, setByReason] = useState({});
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set('date_from', from);
    if (to) p.set('date_to', to);
    if (reason) p.set('reason', reason);
    return p.toString();
  }, [from, to, reason]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/removals?${qs()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not load the removal log');
      setRows(data.items || []);
      setByReason(data.by_reason || {});
      setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, qs]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await fetch(`${API}/api/admin/removals/export?${qs()}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outlaw-removals-${from || 'all'}-${to || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-4" data-testid="removal-log-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <UserMinus className="h-5 w-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-white font-black text-sm">Removal log</p>
            <p className="text-zinc-400 text-xs leading-relaxed">
              Every unsubscribe, bounce, complaint, admin delete, ban and account deletion -
              with the broadcast that caused it. Nothing is removed silently.
            </p>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mt-2" data-testid="removal-log-total">
              {total} removal{total === 1 ? '' : 's'}
              {Object.keys(byReason).length > 0 && (
                <span className="text-zinc-600 normal-case tracking-normal font-normal">
                  {' '}· {Object.entries(byReason).map(([k, v]) => `${REASON_LABEL[k] || k}: ${v}`).join(' · ')}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="removal-from"
            className="h-9 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs text-white outline-none focus:border-red-500"
          />
          <span className="text-zinc-600 text-xs">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="removal-to"
            className="h-9 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs text-white outline-none focus:border-red-500"
          />
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="removal-reason"
            className="h-9 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs text-white outline-none focus:border-red-500"
          >
            <option value="">All reasons</option>
            {Object.entries(REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="removal-refresh"
            className="h-9 px-3 rounded bg-zinc-900 border border-zinc-700 text-white text-xs font-bold inline-flex items-center gap-1.5 hover:bg-zinc-800"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Apply
          </button>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="removal-export"
            className="h-9 px-3 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest inline-flex items-center gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
        </div>
      </div>

      {error && <p className="text-red-400 text-xs mb-3" data-testid="removal-error">{error}</p>}

      {rows.length === 0 ? (
        <p className="text-zinc-500 text-xs py-4" data-testid="removal-empty">
          No removals recorded for this range.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="py-2 px-3 text-gray-400 font-bold text-[10px] uppercase tracking-widest">Email</th>
                <th className="py-2 px-3 text-gray-400 font-bold text-[10px] uppercase tracking-widest">Removed (Sydney)</th>
                <th className="py-2 px-3 text-gray-400 font-bold text-[10px] uppercase tracking-widest">Reason</th>
                <th className="py-2 px-3 text-gray-400 font-bold text-[10px] uppercase tracking-widest">Broadcast / Digest</th>
                <th className="py-2 px-3 text-gray-400 font-bold text-[10px] uppercase tracking-widest">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.email}-${row.removed_at}-${i}`} className="border-b border-zinc-900" data-testid={`removal-row-${row.email}`}>
                  <td className="py-2 px-3 text-white text-xs break-all">{row.email}</td>
                  <td className="py-2 px-3 text-zinc-400 text-xs whitespace-nowrap">{fmtSydney(row)}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${REASON_COLOUR[row.reason] || 'bg-zinc-800 text-zinc-300'}`}>
                      {REASON_LABEL[row.reason] || row.reason}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-zinc-500 text-[11px] font-mono break-all">{row.broadcast_id || '—'}</td>
                  <td className="py-2 px-3 text-zinc-500 text-[11px]">{row.detail || (row.actor ? `by ${row.actor}` : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RemovalLogCard;
